/* L2 iBrushy shell: announcement ticker, products dropdown, mobile menu, footer localization selects.
   Behaviour copied from the live Instant runtime (probed 2026-10-07): ticker speed 50 px/s, --gap 12px, dropdown
   placement bottom-start with a 20px offset, hover with a leave grace, a click on a hover pointer opens (never closes, as live),
   a tap toggles, outside click / Esc close. Ticker width re-measured on resize of the bar or its items and after the fonts load. */
(() => {
  'use strict';

  // ---------- ticker ----------
  function ticker(el) {
    const tracks = el.querySelectorAll(':scope > .ib-ticker__track');
    const items = tracks[0] && tracks[0].querySelector(':scope > .ib-ticker__items');
    if (!items || tracks.length < 2) return;
    tracks[0].querySelectorAll('[data-ib-clone]').forEach((n) => n.remove());
    tracks[1].replaceChildren();
    const w = items.getBoundingClientRect().width;
    const cw = el.getBoundingClientRect().width;
    const m = w > 0 ? Math.max(1, Math.ceil(cw / w)) : 1;
    const copy = () => {
      const c = items.cloneNode(true);
      c.setAttribute('data-ib-clone', '');
      c.setAttribute('aria-hidden', 'true');
      return c;
    };
    for (let i = 1; i < m; i += 1) tracks[0].appendChild(copy());
    for (let i = 0; i < m; i += 1) tracks[1].appendChild(copy());
    const speed = Number(el.dataset.speed) || 50;
    el.style.setProperty('--gap', '12px');
    el.style.setProperty('--play', 'running');
    el.style.setProperty('--direction', el.dataset.reverse === 'true' ? 'reverse' : 'normal');
    el.style.setProperty('--duration', (w * m) / speed + 's');
    el.style.setProperty('--pause-on-hover', el.dataset.pause === 'true' ? 'paused' : 'running');
    el.style.setProperty('--multiplier', String(m));
  }

  // the items' width changes when the web font swaps in: watch the items too, not only the bar
  const ro = 'ResizeObserver' in window ? new ResizeObserver((entries) => {
    new Set(entries.map((e) => e.target.closest('[data-ib-ticker]'))).forEach((el) => el && ticker(el));
  }) : null;
  function initTickers(scope) {
    scope.querySelectorAll('[data-ib-ticker]').forEach((el) => {
      ticker(el);
      const items = el.querySelector(':scope > .ib-ticker__track > .ib-ticker__items');
      if (ro) [el, items].forEach((n) => n && ro.observe(n));
    });
  }

  // ---------- dropdown ----------
  let ddTimer = 0;
  const ddParts = (wrap) => ({ btn: wrap.querySelector('[data-ib-dd-btn]'), dd: wrap.querySelector('[data-ib-dd-wrapper]') });

  function ddPlace(wrap) {
    const { btn, dd } = ddParts(wrap);
    const r = btn.getBoundingClientRect();
    dd.style.cssText = 'z-index:1000;pointer-events:auto;min-width:' + r.width + 'px;min-height:' + r.height + 'px;left:' + r.left + 'px;top:' + (r.bottom + 20) + 'px;';
  }

  function ddSet(wrap, on) {
    const { btn, dd } = ddParts(wrap);
    if (!btn || !dd) return;
    clearTimeout(ddTimer);
    if (on) ddPlace(wrap);
    dd.setAttribute('data-state', on ? 'open' : 'closed');
    btn.setAttribute('data-ib-state', on ? 'active' : 'inactive');
    btn.setAttribute('aria-expanded', on ? 'true' : 'false');
  }

  const openDd = () => document.querySelector('[data-ib-dd-wrapper][data-state="open"]');

  document.addEventListener('mouseover', (e) => {
    const wrap = e.target instanceof Element && e.target.closest('[data-ib-dd]');
    if (wrap && matchMedia('(hover: hover)').matches) ddSet(wrap, true);
  });
  document.addEventListener('mouseout', (e) => {
    const wrap = e.target instanceof Element && e.target.closest('[data-ib-dd]');
    if (!wrap || (e.relatedTarget instanceof Node && wrap.contains(e.relatedTarget))) return;
    clearTimeout(ddTimer);
    ddTimer = setTimeout(() => ddSet(wrap, false), 300);
  });
  window.addEventListener('scroll', () => {
    const dd = openDd();
    if (dd) ddPlace(dd.closest('[data-ib-dd]'));
  }, { passive: true });

  // ---------- mobile menu ----------
  function menuSet(menu, on) {
    menu.setAttribute('data-state', on ? 'open' : 'closed');
    document.documentElement.classList.toggle('ib-menu-lock', on);
    document.querySelectorAll('[data-ib-menu-toggle][aria-controls="' + menu.id + '"]').forEach((b) => b.setAttribute('aria-expanded', on ? 'true' : 'false'));
  }

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const btn = t.closest('[data-ib-dd-btn]');
    if (btn) {
      const wrap = btn.closest('[data-ib-dd]');
      // live: with a mouse, hover opens and clicks keep it open (probed 07/10: 3 clicks, still open); touch toggles
      ddSet(wrap, matchMedia('(hover: hover)').matches || ddParts(wrap).dd.getAttribute('data-state') !== 'open');
      return;
    }
    const dd = openDd();
    if (dd && !dd.contains(t)) ddSet(dd.closest('[data-ib-dd]'), false);

    const toggle = t.closest('[data-ib-menu-toggle]');
    const menu = toggle ? document.getElementById(toggle.getAttribute('aria-controls')) || toggle.closest('[data-ib-menu]') : null;
    if (menu) {
      menuSet(menu, menu.getAttribute('data-state') !== 'open');
      return;
    }
    if (t.matches('[data-ib-menu]')) menuSet(t, false); // backdrop
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const dd = openDd();
    if (dd) ddSet(dd.closest('[data-ib-dd]'), false);
    document.querySelectorAll('[data-ib-menu][data-state="open"]').forEach((m) => menuSet(m, false));
  });

  // ---------- footer localization (native localization form) ----------
  document.addEventListener('change', (e) => {
    if (e.target instanceof Element && e.target.matches('[data-ib-loc-select]') && e.target.form) e.target.form.submit();
  });

  // ---------- init + re-init after a cart re-render of the header ----------
  const init = () => initTickers(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  if (document.fonts) document.fonts.ready.then(init);
  document.addEventListener('ib:sections:rendered', (e) => {
    (e.detail && e.detail.ids || []).forEach((id) => {
      const s = document.getElementById('shopify-section-' + id);
      if (s) initTickers(s);
    });
  });
})();
